// The seat side panel's view state (CHART-PANEL, 2026-10-21): what the panel shows and how wide
// it is. The panel opens on "All dimensions" (every lens the viewer may see, stacked); the
// viewer may instead pick one lens (only that section, expanded with more detail) or "Follow
// chart tab" (the lens the chart's top tabs are on). A pin keeps the chosen mode while other
// seats are clicked, across closing the panel and across visits (remembered per user in this
// browser); unpinned, a new seat or a closed panel goes back to "All dimensions". The panel's
// width is dragged from its left edge and remembered per user. Storage can be missing or
// throw (private windows, blocked site data): every read falls back to the defaults.
import type { Lens } from "@companyos/domain-work/org";
import { useState } from "react";

/**
 * The lenses the panel can show one at a time, in the top tabs' order (Structure is the floor,
 * not a lens of its own here). SEAT-DRAWER-UX §1.1 (decision 3): Overall and Completeness are
 * not tabs of the drawer: Overall is the All view's first block (its weights behind a gear), the
 * setup checklist sits behind the header's ring. Both stay chart lenses; following either opens
 * the drawer on All.
 */
export const PANEL_LENSES = [
  "performance",
  "execution",
  "responsibilities",
  "tasks",
  "finance",
  "keystones",
  // SEAT-DRAWER-UX §2.6: a DE-held seat's track record (a drawer tab, not a chart lens).
  "record",
] as const satisfies readonly (Lens | "record")[];
export type PanelLens = (typeof PANEL_LENSES)[number];

/**
 * W10 polish: the strip's short label per lens (the full name is its tooltip and accessible
 * name, which starts with or contains the short one), short enough that "All" and every tab
 * stay on one line at the panel's 440 px default.
 */
export const PANEL_LENS_SHORT: Record<PanelLens, string> = {
  performance: "Perf",
  execution: "Exec",
  responsibilities: "Resp",
  tasks: "Tasks",
  finance: "Fin",
  keystones: "Prio",
  record: "Record",
};

/** The strip's full name per lens. */
export const PANEL_LENS_FULL: Record<PanelLens, string> = {
  performance: "Performance",
  execution: "Execution",
  responsibilities: "Responsibilities",
  tasks: "Tasks",
  finance: "Finance",
  keystones: "Priorities",
  record: "Record",
};

/** "all" (stacked), "follow" (the chart's tab), or one lens. */
export type PanelMode = "all" | "follow" | PanelLens;
/** What the panel draws once the mode is resolved against the seat. */
export type PanelShown = "all" | PanelLens;
export type PanelPrefs = { mode: PanelMode; pinned: boolean };

export const DEFAULT_PANEL_PREFS: PanelPrefs = { mode: "all", pinned: false };

export const isPanelLens = (v: unknown): v is PanelLens =>
  typeof v === "string" && (PANEL_LENSES as readonly string[]).includes(v);
export const isPanelMode = (v: unknown): v is PanelMode =>
  v === "all" || v === "follow" || isPanelLens(v);

const MODE_KEY = (userId: string) => `companyos.chart.panel-mode.${userId}`;
const WIDTH_KEY = (userId: string) => `companyos.chart.panel-width.${userId}`;

/** The remembered mode: only a pinned one is remembered, so an unpinned panel always opens on the default. */
export function readPanelPrefs(userId: string): PanelPrefs {
  try {
    const raw = window.localStorage.getItem(MODE_KEY(userId));
    if (!raw) return DEFAULT_PANEL_PREFS;
    const v = JSON.parse(raw) as { mode?: unknown };
    return isPanelMode(v.mode) ? { mode: v.mode, pinned: true } : DEFAULT_PANEL_PREFS;
  } catch {
    return DEFAULT_PANEL_PREFS;
  }
}

export function writePanelPrefs(userId: string, prefs: PanelPrefs): void {
  try {
    if (prefs.pinned)
      window.localStorage.setItem(MODE_KEY(userId), JSON.stringify({ mode: prefs.mode }));
    else window.localStorage.removeItem(MODE_KEY(userId));
  } catch {}
}

/**
 * What the panel draws: "follow" reads the chart's lens; a lens the viewer may not see on this
 * seat (the server left it out) falls back to "all" — the pinned mode stays, so the next seat
 * that carries it opens on it again.
 */
export function resolvePanelMode(
  mode: PanelMode,
  chartLens: Lens,
  available: readonly PanelLens[],
): PanelShown {
  const want = mode === "follow" ? chartLens : mode;
  if (want === "all") return "all";
  return isPanelLens(want) && available.includes(want) ? want : "all";
}

/** The strip's order: "All", then each lens the viewer may see; the arrows wrap around. */
export function cyclePanel(
  shown: PanelShown,
  available: readonly PanelLens[],
  dir: 1 | -1,
): PanelShown {
  const order: PanelShown[] = ["all", ...available];
  const at = Math.max(0, order.indexOf(shown));
  return order[(at + dir + order.length) % order.length] ?? "all";
}

/**
 * Whether the All view's "Seat setup" line ("3 of 7 in place · Fix", opening the ring's
 * checklist) is drawn: only in "All dimensions", while the seat is not fully set up (the
 * header's ring already says 100 %), and always while the chart is on Completeness (it pins the
 * line to the top). While the dimensions load (`score` undefined) it waits.
 */
export function showSeatSetup(
  score: number | undefined,
  shown: PanelShown,
  chartLens?: Lens,
): boolean {
  if (shown !== "all") return false;
  if (chartLens === "completeness") return true;
  return score === undefined || score < 100;
}

/**
 * The panel's mode across seats: a pinned mode is kept (and remembered), an unpinned one goes
 * back to the default whenever another seat is opened. Closing the panel unmounts it, so the
 * next open reads the pinned mode or the default.
 */
export function usePanelMode(userId: string, seatId: string) {
  const [prefs, setPrefs] = useState<PanelPrefs>(() => readPanelPrefs(userId));
  const [forSeat, setForSeat] = useState(seatId);
  if (forSeat !== seatId) {
    setForSeat(seatId);
    if (!prefs.pinned && prefs.mode !== DEFAULT_PANEL_PREFS.mode) setPrefs(DEFAULT_PANEL_PREFS);
  }
  const setMode = (mode: PanelMode) => {
    const next = { ...prefs, mode };
    setPrefs(next);
    if (next.pinned) writePanelPrefs(userId, next);
  };
  const togglePin = () => {
    const next = { ...prefs, pinned: !prefs.pinned };
    setPrefs(next);
    writePanelPrefs(userId, next);
  };
  return { ...prefs, setMode, togglePin };
}

/** The panel's width: 440 px by default, 360 (the design spec's panel) to 720. */
export const PANEL_WIDTH = { min: 360, default: 440, max: 720 } as const;
/** The canvas keeps at least this much beside the panel. */
const CANVAS_MIN = 420;

export function clampPanelWidth(w: number, viewport?: number): number {
  const room = viewport ? Math.max(PANEL_WIDTH.min, viewport - CANVAS_MIN) : PANEL_WIDTH.max;
  const max = Math.min(PANEL_WIDTH.max, room);
  if (!Number.isFinite(w)) return Math.min(PANEL_WIDTH.default, max);
  return Math.round(Math.min(max, Math.max(PANEL_WIDTH.min, w)));
}

export function readPanelWidth(userId: string): number {
  try {
    const v = Number(window.localStorage.getItem(WIDTH_KEY(userId)));
    return v > 0 ? clampPanelWidth(v) : PANEL_WIDTH.default;
  } catch {
    return PANEL_WIDTH.default;
  }
}

export function writePanelWidth(userId: string, w: number): void {
  try {
    if (w === PANEL_WIDTH.default) window.localStorage.removeItem(WIDTH_KEY(userId));
    else window.localStorage.setItem(WIDTH_KEY(userId), String(w));
  } catch {}
}
